import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import { OrderTableRow } from './OrderTableRow'
import { useMyOrdersData } from './useMyOrdersData'
import { TimeTrackingControlBar } from '../../components/TimeTrackingControlBar'
import {
  TimeTrackingTab,
  FilterValues,
  MyOrdersTabProps,
  DisplayFormat,
  OrderScope,
} from '../../types'
import { getMonthRange } from '../../utils/dateUtils'
import { Column } from '@/Atomic/Templates/Table'
import { DisplayCountIndicator } from '@/Components/DisplayCountIndicator'
import { Table } from '@/Components/Table'

export const MyOrdersTab = connect(
  ({
    navigateToTab,
    navigateToResource,
    initialFilters,
    monthRange,
    onMonthRangeChange,
    onFilterChange,
    currentUserId,
  }: MyOrdersTabProps) => {
    const { t } = useTranslation()
    const filters: FilterValues = initialFilters || {
      status: null,
      team: null,
      customer: null,
      order: null,
      workPackage: null,
    }
    const [displayFormat, setDisplayFormat] = useState<DisplayFormat>('days')
    const [orderScope, setOrderScope] = useState<OrderScope>('myOrders')

    const { orders, customers, summaryMetrics } = useMyOrdersData(
      monthRange,
      currentUserId,
      filters,
      orderScope,
    )

    const handleFilterChange = (newFilters: FilterValues) => {
      onFilterChange(newFilters)
    }

    const tableColumns = [
      {
        accessor: 'orderType',
        title: t('timeTracking.columns.orderType'),
        variant: Column.WIDTH_120PX,
      },
      {
        accessor: 'orderTitle',
        title: t('timeTracking.columns.orderTitle'),
        variant: Column.MAIN,
      },
      {
        accessor: 'customerName',
        title: t('timeTracking.columns.customerName'),
        variant: Column.WIDTH_260PX,
      },
      {
        accessor: 'revenue',
        title: t('timeTracking.columns.revenue'),
        variant: Column.WIDTH_120PX,
      },
      {
        accessor: 'totalBudget',
        title: t('timeTracking.columns.totalBudget'),
        variant: Column.WIDTH_140PX,
      },
      {
        accessor: 'usedBudget',
        title: t('timeTracking.columns.usedBudget'),
        variant: Column.WIDTH_140PX,
      },
      {
        accessor: 'remainingBudget',
        title: t('timeTracking.columns.remainingBudget'),
        variant: Column.WIDTH_160PX,
      },
      {
        accessor: 'status',
        title: t('timeTracking.columns.status'),
        variant: Column.WIDTH_120PX,
      },
    ]

    return (
      <div>
        <TimeTrackingControlBar
          weekRange={monthRange}
          statusOptions={[]}
          teamOptions={[]}
          customerOptions={customers}
          initialFilters={filters}
          displayFormat={displayFormat}
          onDisplayFormatChange={setDisplayFormat}
          orderScope={orderScope}
          onOrderScopeChange={setOrderScope}
          showWithRevenue={filters.showWithRevenue ?? false}
          onShowWithRevenueChange={(value) =>
            onFilterChange({ ...filters, showWithRevenue: value })
          }
          onWeekChange={(range) => {
            onMonthRangeChange(range)
            window.dispatchEvent(new Event('focus'))
          }}
          onNavigateWeek={(direction) => {
            const currentDate = new Date(monthRange.startDate)
            const newDate = new Date(currentDate)
            newDate.setMonth(
              currentDate.getMonth() + (direction === 'next' ? 1 : -1),
            )
            onMonthRangeChange(getMonthRange(newDate))
            window.dispatchEvent(new Event('focus'))
          }}
          onCurrentWeek={() => {
            onMonthRangeChange(getMonthRange(new Date()))
            window.dispatchEvent(new Event('focus'))
          }}
          onFilterChange={handleFilterChange}
          currentTab={TimeTrackingTab.MY_ORDERS}
        />
        <div className="my-team-summary-cards">
          <div className="my-team-summary-card">
            <div className="my-team-summary-label">
              {t('timeTracking.billing.revenue')}
            </div>
            <div className="my-team-summary-value">
              {displayFormat === 'days'
                ? `${summaryMetrics.revenueDays}d`
                : `${summaryMetrics.revenue}`}
            </div>
          </div>

          <>
            <div className="my-team-summary-card">
              <div className="my-team-summary-label">
                {t('timeTracking.billing.totalBudget')}
              </div>
              <div className="my-team-summary-value">
                {displayFormat === 'days'
                  ? `${summaryMetrics.totalDays}d`
                  : `${summaryMetrics.totalBudget}`}
              </div>
            </div>
            <div className="my-team-summary-card">
              <div className="my-team-summary-label">
                {t('timeTracking.billing.budgetUsed')}
              </div>
              <div className="my-team-summary-value">
                {displayFormat === 'days'
                  ? `${summaryMetrics.usedDays}d`
                  : `${summaryMetrics.usedBudget}`}
              </div>
            </div>
            <div className="my-team-summary-card">
              <div className="my-team-summary-label">
                {t('timeTracking.billing.remainingBudget')}
              </div>
              <div className="my-team-summary-value">
                {displayFormat === 'days'
                  ? `${summaryMetrics.remainingDays}d`
                  : `${summaryMetrics.remainingBudget}`}
              </div>
            </div>
          </>
        </div>

        <div className="row">
          <div className="col-sm-12">
            <DisplayCountIndicator />
            {!orders.length ? (
              <div>{t('timeTracking.noData')}</div>
            ) : (
              <>
                <div className="card position-relative">
                  <div>
                    <Table
                      columns={tableColumns}
                      className="time-tracking-compact-table time-tracking-orders-table mb-0"
                      wrapperClassName="table-scroll-shadow"
                    >
                      {orders.map((item) => (
                        <OrderTableRow
                          key={item.orderId}
                          row={item}
                          navigateToTab={navigateToTab}
                          navigateToResource={navigateToResource}
                          monthRange={monthRange}
                          displayFormat={displayFormat}
                          teamLeaderId={currentUserId}
                        />
                      ))}
                    </Table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  },
)
